import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  Autocomplete,
  ButtonGroup,
  Divider,
  FormControlLabel,
  Switch
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ERPSpreadsheet from './ERPSpreadsheet';
import salesPercentageApi from '../services/salesPercentageApi';
import { format } from 'date-fns';

const SalesPercentage = () => {
  // State for data
  const [spreadsheetData, setSpreadsheetData] = useState({});
  const [itemConfig, setItemConfig] = useState(null);
  const [filterOptions, setFilterOptions] = useState(null);
  const [metadata, setMetadata] = useState(null);

  // State for filters
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)), // Last month
    endDate: new Date(),
    locationIds: [],
    percentageType: 'total',
    metricType: 'quantity_percentage'
  });

  // State for UI
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(true);

  // Available metrics for the spreadsheet
  const availableMetrics = [
    filters.metricType || 'quantity_percentage'
  ];

  // Initialize data on component mount
  useEffect(() => {
    initializeData();
  }, []);

  const initializeData = async () => {
    setIsLoading(true);
    try {
      console.log('🔄 Initializing sales percentage data...');
      
      // Fetch filter options and item config in parallel
      const [filterOptionsResult, itemConfigResult] = await Promise.all([
        salesPercentageApi.getFilterOptions(),
        salesPercentageApi.getItemConfig()
      ]);

      console.log('📊 Filter options result:', filterOptionsResult);
      console.log('📋 Item config result:', itemConfigResult);

      if (filterOptionsResult?.success && itemConfigResult?.success) {
        setFilterOptions(filterOptionsResult.data);
        setItemConfig(itemConfigResult.data);
        
        console.log('✅ Configuration loaded successfully');
        
        // Load initial data
        await loadSpreadsheetData();
      } else {
        const errors = [];
        if (!filterOptionsResult?.success) {
          errors.push(`Filter options: ${filterOptionsResult?.message || 'Unknown error'}`);
        }
        if (!itemConfigResult?.success) {
          errors.push(`Item config: ${itemConfigResult?.message || 'Unknown error'}`);
        }
        throw new Error(`Initialization failed: ${errors.join(', ')}`);
      }
    } catch (err) {
      console.error('❌ Error initializing data:', err);
      
      // More specific error messages
      let errorMessage = 'Failed to initialize sales percentage data.';
      
      if (err.message?.includes('Network Error') || err.message?.includes('fetch')) {
        errorMessage = 'Network error: Cannot connect to the server. Please check your connection.';
      } else if (err.message?.includes('404')) {
        errorMessage = 'API endpoints not found. Please ensure the backend is running.';
      } else if (err.message?.includes('401') || err.message?.includes('403')) {
        errorMessage = 'Authentication error. Please refresh the page and try again.';
      } else if (err.message?.includes('500')) {
        errorMessage = 'Server error. Please try again in a few moments.';
      } else if (err.message?.includes('Initialization failed:')) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSpreadsheetData = useCallback(async () => {
    if (!filters.startDate || !filters.endDate) {
      setError('Please select both start and end dates');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const filterParams = {
        startDate: format(filters.startDate, 'yyyy-MM-dd'),
        endDate: format(filters.endDate, 'yyyy-MM-dd'),
        locationIds: filters.locationIds,
        percentageType: filters.percentageType,
        metricType: filters.metricType
      };

      console.log('📈 Loading spreadsheet data with params:', filterParams);

      const result = await salesPercentageApi.getSpreadsheetData(filterParams);
      
      console.log('📊 Spreadsheet data result:', result);
      
      if (result?.success) {
        setSpreadsheetData(result.data || {});
        setMetadata(result.metadata || null);
        
        if (Object.keys(result.data || {}).length === 0) {
          console.log('⚠️ No data returned from API');
        }
      } else {
        throw new Error(result?.message || 'Failed to fetch spreadsheet data');
      }
    } catch (err) {
      console.error('❌ Error loading spreadsheet data:', err);
      
      let errorMessage = 'Failed to load sales percentage data.';
      
      if (err.message?.includes('Network Error')) {
        errorMessage = 'Network error: Cannot connect to the server.';
      } else if (err.message?.includes('404')) {
        errorMessage = 'Spreadsheet data endpoint not found.';
      } else if (err.message?.includes('500')) {
        errorMessage = 'Server error while loading data. Please try again.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setSpreadsheetData({});
      setMetadata(null);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  // Load data when filters change
  useEffect(() => {
    if (itemConfig && filterOptions) {
      const timer = setTimeout(() => {
        loadSpreadsheetData();
      }, 300); // Debounce

      return () => clearTimeout(timer);
    }
  }, [filters, itemConfig, filterOptions, loadSpreadsheetData]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)),
      endDate: new Date(),
      locationIds: [],
      percentageType: 'total',
      metricType: 'quantity_percentage'
    });
  };

  const getMetricDisplayName = (metricType) => {
    const metricMap = {
      'quantity_percentage': 'Quantity %',
      'revenue_percentage': 'Revenue %',
      'quantity': 'Quantity',
      'revenue': 'Revenue'
    };
    return metricMap[metricType] || metricType;
  };

  const getPercentageTypeDisplayName = (percentageType) => {
    const typeMap = {
      'total': 'Total Sales',
      'type': 'Product Type',
      'location': 'Location',
      'timeRange': 'Time Range'
    };
    return typeMap[percentageType] || percentageType;
  };

  if (isLoading && !itemConfig) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={40} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading Sales Percentage Analysis...
        </Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3, maxWidth: '100%', margin: '0 auto' }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AnalyticsIcon color="primary" />
            Sales Percentage Analysis
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Analyze sales performance across products with percentage breakdowns and comparisons
          </Typography>
        </Box>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Filters Section */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterAltIcon />
              Filters & Controls
            </Typography>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={showFilters}
                    onChange={(e) => setShowFilters(e.target.checked)}
                    size="small"
                  />
                }
                label="Show Filters"
              />
              <Button
                onClick={clearFilters}
                size="small"
                sx={{ ml: 2 }}
              >
                Clear All
              </Button>
            </Box>
          </Box>

          {showFilters && (
            <Grid container spacing={3}>
              {/* Date Range */}
              <Grid item xs={12} md={6}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <DatePicker
                      label="Start Date"
                      value={filters.startDate}
                      onChange={(newValue) => handleFilterChange('startDate', newValue)}
                      renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small'
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DatePicker
                      label="End Date"
                      value={filters.endDate}
                      onChange={(newValue) => handleFilterChange('endDate', newValue)}
                      renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small'
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Locations */}
              <Grid item xs={12} md={6}>
                {filterOptions?.locations && (
                  <Autocomplete
                    multiple
                    options={filterOptions.locations}
                    getOptionLabel={(option) => option.location_name}
                    value={filterOptions.locations.filter(loc => 
                      filters.locationIds.includes(loc.location_id)
                    )}
                    onChange={(event, newValue) => {
                      handleFilterChange('locationIds', newValue.map(loc => loc.location_id));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Locations"
                        placeholder="Select locations (all if empty)"
                        size="small"
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option.location_name}
                          size="small"
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                  />
                )}
              </Grid>

              {/* Percentage Type */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Percentage Calculation</InputLabel>
                  <Select
                    value={filters.percentageType}
                    label="Percentage Calculation"
                    onChange={(e) => handleFilterChange('percentageType', e.target.value)}
                  >
                    {filterOptions?.percentageTypes?.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Metric Type */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Metric Type</InputLabel>
                  <Select
                    value={filters.metricType}
                    label="Metric Type"
                    onChange={(e) => handleFilterChange('metricType', e.target.value)}
                  >
                    {filterOptions?.metricTypes?.map((metric) => (
                      <MenuItem key={metric.value} value={metric.value}>
                        {metric.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Action Buttons */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={loadSpreadsheetData}
                    disabled={isLoading}
                  >
                    Refresh Data
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}
        </Paper>

        {/* Summary Cards */}
        {metadata && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="primary" gutterBottom>
                    Total Items
                  </Typography>
                  <Typography variant="h4">
                    {metadata.totalItems?.toLocaleString() || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="primary" gutterBottom>
                    Total Quantity
                  </Typography>
                  <Typography variant="h4">
                    {metadata.totalQuantity?.toLocaleString() || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="primary" gutterBottom>
                    Total Revenue
                  </Typography>
                  <Typography variant="h4">
                    ${metadata.totalRevenue?.toLocaleString(undefined, { 
                      minimumFractionDigits: 2, 
                      maximumFractionDigits: 2 
                    }) || '0.00'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="primary" gutterBottom>
                    Analysis Type
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {getMetricDisplayName(filters.metricType)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    vs {getPercentageTypeDisplayName(filters.percentageType)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Active Filters Display */}
        {(filters.locationIds.length > 0 || 
          filters.percentageType !== 'total' || 
          filters.metricType !== 'quantity_percentage') && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingUpIcon fontSize="small" />
              Active Filters:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {filters.locationIds.length > 0 && (
                <Chip
                  label={`${filters.locationIds.length} Location(s) Selected`}
                  size="small"
                  variant="outlined"
                  color="primary"
                />
              )}
              {filters.percentageType !== 'total' && (
                <Chip
                  label={`% Type: ${getPercentageTypeDisplayName(filters.percentageType)}`}
                  size="small"
                  variant="outlined"
                  color="secondary"
                />
              )}
              {filters.metricType !== 'quantity_percentage' && (
                <Chip
                  label={`Metric: ${getMetricDisplayName(filters.metricType)}`}
                  size="small"
                  variant="outlined"
                  color="info"
                />
              )}
              <Chip
                label={`${format(filters.startDate, 'MMM dd')} - ${format(filters.endDate, 'MMM dd, yyyy')}`}
                size="small"
                variant="outlined"
              />
            </Box>
          </Paper>
        )}

        {/* Spreadsheet */}
        {itemConfig && (
          <Paper sx={{ p: 2, overflow: 'hidden' }}>
            <ERPSpreadsheet
              data={spreadsheetData}
              itemConfig={itemConfig}
              loading={isLoading}
              metricType={filters.metricType}
              percentageType={filters.percentageType}
            />
          </Paper>
        )}

        {/* Debug Information */}
        {process.env.NODE_ENV === 'development' && (
          <Paper sx={{ p: 2, mt: 2, bgcolor: 'grey.100' }}>
            <Typography variant="subtitle2" gutterBottom>Debug Information:</Typography>
            <pre style={{ fontSize: '12px', whiteSpace: 'pre-wrap' }}>
              {JSON.stringify({
                itemConfigLoaded: !!itemConfig,
                filterOptionsLoaded: !!filterOptions,
                spreadsheetDataKeys: Object.keys(spreadsheetData).length,
                currentFilters: filters,
                metadata
              }, null, 2)}
            </pre>
          </Paper>
        )}

        {/* No Data Message */}
        {!isLoading && itemConfig && Object.keys(spreadsheetData).length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Sales Data Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your filters or date range to find sales data.
            </Typography>
          </Paper>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default SalesPercentage; 